import React, { useId, useMemo, useRef, useState } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/20/solid';
import './ui.css';

// Every word typed must match the start of a word in the item's text, so
// "sa wa" finds Sam Walker but "am" doesn't find Sam
const SPLIT = /[\s.@,()'-]+/;
export function matches(item, query) {
  const words = `${item.label} ${item.search || ''}`.toLowerCase().split(SPLIT).filter(Boolean);
  return query.toLowerCase().split(SPLIT).filter(Boolean).every(t => words.some(w => w.startsWith(t)));
}

/**
 * Choosing from a long list (people, contacts, clubs): a search box. Tap it to
 * see everything in a scrolling list, or type part of a name. Anything that
 * can grow past about 15 items uses this instead of a drop-down.
 *
 * items: [{ id, label, detail?, search? }]. `detail` is the grey second line
 *   that tells apart two people with the same name; `search` is extra text to
 *   match on (email, children's names) that isn't shown.
 * value: the chosen id ('' for none), or a list of ids when `multiple`
 * onChange(id | ids)
 * placeholder: e.g. "Search by name, email or child…"
 * label: what it's for, read out by screen readers ("Member")
 * id: put the same id on a <label htmlFor> to label it visibly
 */
export default function Picker({ items, value, onChange, multiple = false, placeholder = 'Search…', label, id, required, disabled, noneText = 'No one matches. Try a surname or email.' }) {
  const ownId = useId();
  const inputId = id || `${ownId}-q`;
  const listId = `${ownId}-list`;
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const inputRef = useRef(null);
  const chosen = multiple ? (value || []) : (value ? [value] : []);
  const byId = useMemo(() => new Map(items.map(i => [i.id, i])), [items]);

  const shown = useMemo(
    () => items.filter(i => !(multiple && chosen.includes(i.id)) && (!query.trim() || matches(i, query))),
    // chosen is derived from value
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, query, multiple, value],
  );

  const choose = (item) => {
    if (multiple) {
      onChange([...chosen, item.id]);
      setQuery('');
      setActive(-1);
      inputRef.current?.focus();
    } else {
      onChange(item.id);
      setQuery('');
      setOpen(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive(a => Math.min(a + 1, shown.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') {
      if (!open) return;
      e.preventDefault();
      const item = shown[active >= 0 ? active : 0];
      if (item && (active >= 0 || query.trim())) choose(item);
    } else if (e.key === 'Escape' && open) { e.stopPropagation(); e.nativeEvent.stopImmediatePropagation?.(); setOpen(false); }
  };

  const single = !multiple && chosen.length ? byId.get(chosen[0]) : null;

  return (
    <div className="ui-picker">
      {single ? (
        <div className="ui-picker__chosen">
          <span id={inputId}>{single.label}</span>
          {!disabled && (
            <button type="button" className="ui-icon-btn" aria-label={`Change ${label ? label.toLowerCase() : 'choice'}`} onClick={() => { onChange(''); setTimeout(() => inputRef.current?.focus(), 0); }}>
              <XMarkIcon aria-hidden="true" />
            </button>
          )}
        </div>
      ) : (
        <div className="ui-picker__search">
          <input
            ref={inputRef}
            id={inputId}
            className="bk-input"
            type="search"
            role="combobox"
            aria-label={label}
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
            autoComplete="off"
            placeholder={placeholder}
            value={query}
            disabled={disabled}
            required={required && !chosen.length}
            onChange={(e) => { setQuery(e.target.value); setActive(-1); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 120)}
            onKeyDown={onKeyDown}
          />
          <MagnifyingGlassIcon aria-hidden="true" />
          {open && (
            <ul className="ui-picker__list" id={listId} role="listbox" aria-label={label}>
              {shown.length ? shown.map((item, i) => (
                <li
                  key={item.id}
                  id={`${listId}-${i}`}
                  role="option"
                  aria-selected={i === active}
                  className="ui-picker__option"
                  onMouseDown={(e) => { e.preventDefault(); choose(item); }}
                >
                  <span>{item.label}</span>
                  {item.detail && <span className="ui-picker__detail">{item.detail}</span>}
                </li>
              )) : <li className="ui-picker__none" role="option" aria-disabled="true" aria-selected="false">{noneText}</li>}
            </ul>
          )}
        </div>
      )}
      {multiple && chosen.length > 0 && (
        <div className="ui-picker__chips">
          {chosen.map(cid => {
            const item = byId.get(cid);
            if (!item) return null;
            return (
              <span key={cid} className="ui-picker__chip">
                {item.label}
                <button type="button" className="ui-icon-btn" aria-label={`Remove ${item.label}`} onClick={() => onChange(chosen.filter(c => c !== cid))}>
                  <XMarkIcon aria-hidden="true" />
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
